import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { formatString } from '@frontend/sports/common/core/utils/string';
import { WINDOW } from '@frontend/vanilla/core';
import { Subject, distinctUntilChanged, filter, fromEvent, merge, switchMap, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'ms-my-bets-taxation-info',
    templateUrl: './taxation-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxationInfoComponent implements OnInit, OnChanges {
    @Input() isVisible = false;
    private isVisible$ = new Subject<boolean>();
    @Output() isVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    private readonly destroyRef = inject(DestroyRef);
    readonly #window = inject(WINDOW);

    constructor(
        public sitecore: Sitecore,
        private hostElement: ElementRef,
    ) {}

    ngOnInit(): void {
        this.subscribeToClickEvents();
    }

    ngOnChanges(_changes: ISimpleChanges<TaxationInfoComponent>): void {
        this.isVisible$.next(this.isVisible);
    }

    close(): void {
        this.isVisibleChange.emit(false);
    }

    openTaxationHelp(): void {
        const url = this.sitecore.betslip.links.taxation?.url;
        if (!url) {
            return;
        }

        this.#window.open(url, '_blank');
    }

    get taxationHelpText(): string {
        const furtherInfoText = this.sitecore.betslip.editBet.taxationFurtherInfoText;
        const taxationHelpText = formatString(furtherInfoText, `<span>${this.sitecore.betslip.editBet.bwinHelpPage}</span>`);

        return taxationHelpText;
    }

    private subscribeToClickEvents(): void {
        const clickOutHandlerStream$ = fromEvent(window, 'click').pipe(tap(() => this.close()));

        const hostClickHandlerStream$ = fromEvent<MouseEvent>(this.hostElement.nativeElement, 'click').pipe(
            tap((event) => {
                event.preventDefault();
                event.stopPropagation();
            }),
        );

        // each click event stream should stop when isVisible changes to false
        const merged$ = merge(
            ...[clickOutHandlerStream$, hostClickHandlerStream$].map((handler$) => handler$.pipe(takeUntil(this.isVisible$.pipe(filter((x) => !x))))),
        );

        this.isVisible$
            .pipe(
                distinctUntilChanged(),
                filter((x) => x),
                switchMap(() => merged$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }
}
